import { fieldMarkers } from "../../../common/constants/export";

export const terms = Object.freeze({
    gainedAgents: 'Gained Agents',
    gainedAgentsFrom: 'Gained Agents From',
    lostAgents: 'Lost Agents',
    lostAgentsTo: 'Lost Agents To',
    netAgentsGained: 'Net Agents Gained',
    netDollarVolume: 'Net $ Volume',
    officeDetails: 'Office Details',
    viewBreakdown: 'View Breakdown',
    officeBreakdownAndGrowth: 'Office Breakdown & Growth',
    selectedOffices: 'Selected Offices',
    selectedOfficeOrOffices: 'Selected Office(s)',
    getGrewByAgents: (officesTitle, agentsAmount) => `${officesTitle} grew by ${agentsAmount} agent(s).`,
    getShrunkByAgents: (officesTitle, agentsAmount) => `${officesTitle} shrunk by ${agentsAmount} agent(s).`,
    getZeroAgents: officesTitle => `${officesTitle} experienced no change in agent count.`,
    gained: 'Gained',
    lost: 'Lost',
    netGained: 'Net Gained',
    agentsLost: 'Agents Lost',
    agentsGained: 'Agents Gained',
    from: ' from ',
    to: ' to ',
});

export const tooltips = Object.freeze({
    // Office information
    officeName: 'The name of the office as reflected in the MLS data',
    officeId: 'ID code of the office (assigned by MLS)',
    officeAddress: 'The office\'s physical and/or mailing address as reflected in the MLS data',
    city: 'The city associated with the office as reflected in the MLS data',
    zip: 'The USPS zip code associated with the office as reflected in the MLS data',

    // Agents
    lostAgentsTo: 'The number of agents who left another office in your MLS and joined one of your offices or the selected offices',
    gainedAgentsFrom: 'The number of agents who left one of your offices or the selected offices and joined another office in your MLS',
    netAgentsGained: 'The difference between the number of agents gained and the number of agents lost',

    agentsGained: 'The number of agents who left another office in your MLS and joined one of your offices or the selected office',
    agentsLost: 'The number of agents who left one of your offices or the selected office and joined another office in your MLS',

    // Potential Impact
    netDollarVolume: `The net difference in Total $ Volume (based on the listings sold during the 365 Days prior to the agent's move) for the agents who left one of your offices and the agents who joined one of your offices`,


    // Dashboard Indicators
    dashboardAgentsGained: `The number of agents who left another office in your MLS and joined one of your offices`,
    dashboardAgentsLost: `The number of agents who left one of your offices and joined another office in your MLS`,
    dashboardPotentialGain: `The net Total $ Volume (based on the listings sold during the Last 365 Days prior to the agent's move) for the agents who joined one of your offices`,
    dashboardPotentialLoss: `The net Total $ Volume (based on the listings sold during the Last 365 Days prior to the agent's move) for the agents who left one of your offices`
});

export const indicatorLabels = Object.freeze({
    agentsGained: 'Agents Gained',
    agentsLost: 'Agents Lost',
    potentialGain: 'Potential Gain (Volume)',
    potentialLoss: 'Potential Loss (Volume)',
});

export const indicatorsSettingsByKeyName = Object.freeze({
    otherAgentsGained: {
        id: 'otherAgentsGained',
        label: indicatorLabels.agentsGained,
        tooltip: tooltips.dashboardAgentsGained
    },
    otherAgentsLost: {
        id: 'otherAgentsLost',
        label: indicatorLabels.agentsLost,
        tooltip: tooltips.dashboardAgentsLost
    },
    otherVolumeGained: {
        id: 'otherVolumeGained',
        label: indicatorLabels.potentialGain,
        tooltip: tooltips.dashboardPotentialGain
    },
    otherVolumeLost: {
        id: 'otherVolumeLost',
        label: indicatorLabels.potentialLoss,
        tooltip: tooltips.dashboardPotentialLoss
    }
});

export const commonAgMovementTooltips = {
    agentCity: { id: 'agentCity', label: 'Agent City', tip: 'The city associated with the agent as reflected in the MLS data' },
    agentEmail: { id: 'agentEmail', label: 'Agent Email', tip: 'The agent\'s email address as reflected in the MLS data' },
    agentName: { id: 'agentName', label: 'Agent Name', tip: 'Name of the Agent' },
    agentPhone1: { id: 'agentPhone1', label: 'Agent Phone 1', tip: 'A telephone number for the agent as reflected in the MLS data' },
    agentPhone2: { id: 'agentPhone2', label: 'Agent Phone 2', tip: 'A telephone number for the agent as reflected in the MLS data' },
    agentPhone3: { id: 'agentPhone3', label: 'Agent Phone 3', tip: 'A telephone number for the agent as reflected in the MLS data' },
    agentStreetName: { id: 'agentStreetName', label: 'Agent Address', tip: 'The agent\'s alternate  physical and/or mailing address as reflected in the MLS data' },
    agentZipCode: { id: 'agentZipCode', label: 'Agent Zip', tip: 'The USPS zip code associated with the agent as reflected in the MLS data' },
    daysSinceMove: { id: 'daysSinceMove', label: '# Days', tip: 'The number of days since the agent moved to a different office' },
    moveDate: { id: 'moveDate', label: 'Move Date', tip: 'Date when agents office changed' },
    officeCity: { id: 'officeCity', label: 'City', tip: 'The city associated with the office as reflected in the MLS data' },
    officeId: { id: 'officeId', label: 'Office ID', tip: 'ID Code assigned by MLS' },
    officeStreetName: { id: 'officeStreetName', label: 'Office Address', tip: 'The office\'s physical and/or mailing address as reflected in the MLS data' },
    officeZipCode: { id: 'officeZipCode', label: 'Zip', tip: 'The USPS zip code associated with the office as reflected in the MLS data' },
    percentOf365DaysWithOffice: { id: 'percentOf365DaysWithOffice', label: '% of 365', tip: 'The percentage of a full year that the agent has been with the office' },
    volumeDifference: { id: 'volumeDifference', label: 'Difference', tip: 'The difference between the agent\'s total $ volume after the move and the agent\'s $ volume yardstick ' },
    volumeGrowth: { id: 'volumeGrowth', label: '% Growth', tip: 'A percentage measurement of the agent\'s actual $ volume performance relative to their $ volume yardstick  ' },
    volumePostMove: { id: 'volumePostMove', label: '$ Volume', tip: 'The agent\'s total $ volume since moving to a different office' },
    volumePreMove: { id: 'volumePreMove', label: '$ Volume', tip: 'The agent\'s total $ volume for the 365 days prior to the move to a different office' },
    volumeYardstick: { id: 'volumeYardstick', label: '$ Volume Yardstick', tip: 'The amount of post-move $ volume that would be necessary to keep the agent on pace with their 365 day pre-move $ volume yardstick' },
};

export const agentsGainedTooltips = Object.freeze({
    ...commonAgMovementTooltips,
    officeName: { id: 'officeName', label: 'Office From', tip: 'The office the agent left to join one of your offices' },
});

export const lostAgentsTooltips = Object.freeze({
    ...commonAgMovementTooltips,
    officeName: { id: 'officeName', label: 'Office To', tip: 'The office the agent joined after leaving one of your offices' },
});

export const exportFilenameTemplates = {
    amSearchResults: `Agent Movement Report - ${fieldMarkers.DATE}`,
    breakdownResults: `Agent Movement - ${fieldMarkers.AM_BREAKDOWN_TAB}${fieldMarkers.FROM_OR_TO}${fieldMarkers.OFFICE} - ${fieldMarkers.DATE}`,
    fromBreakdownGrowthResults: `Agent Movement - ${fieldMarkers.OFFICE} ${fieldMarkers.AM_BREAKDOWN_TAB} - ${fieldMarkers.DATE}`,
};
